import React, { useCallback, useEffect, useState } from "react";

import { apiDownload, apiGet, apiPost } from "../../Api/Client.js";
import { Badge, Button, Card } from "../../Ui/index.jsx";
import { wholeNumberDraft } from "./settingDraft.js";

// The company admin's two cards (Entry 20, design A), shown inside the admin panel at /admin/:
// Modules and add-ons, and Company settings. The old /plan-and-plugins/ address redirects there.

// Modules and add-ons: the company admin's switches (Entry 20, design A). Products with their
// add-ons nested underneath. Switching a product off switches its add-ons off with it (shown,
// not just enforced). Nothing about plans or prices: what is not included just says so.
export function ModulesAndAddOns({ onPluginsChanged }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busyCode, setBusyCode] = useState("");
  const [open, setOpen] = useState({});
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    apiGet("/EnterpriseCore/plugins/")
      .then((res) => { setData(res); setError(""); })
      .catch((err) => setError(err?.payload?.message || err?.message || "Could not load your products."));
  }, []);

  useEffect(() => { load(); }, [load]);

  const items = data?.plugins || [];
  const products = items.filter((i) => i.kind === "product");
  const addonsOf = (code) => items.filter((i) => i.kind === "addon" && i.parent === code);
  const platform = items.filter((i) => i.kind === "platform");
  const query = q.trim().toLowerCase();
  const matches = (item) => !query || [item.name, item.code, item.summary].join(" ").toLowerCase().includes(query);

  const send = async (item, action, body) => {
    setBusyCode(item.code);
    setError("");
    try {
      await apiPost(`/EnterpriseCore/plugins/${encodeURIComponent(item.code)}/${action}/`, body);
      load();
      if (onPluginsChanged) onPluginsChanged();
    } catch (err) {
      setError(err?.payload?.message || err?.message || "That change was not made.");
    } finally {
      setBusyCode("");
    }
  };

  const toggle = (item) => {
    const next = item.enabled ? "disabled" : "enabled";
    if (next === "disabled") {
      const kids = item.kind === "product" ? addonsOf(item.code).filter((a) => a.enabled).map((a) => a.name) : [];
      const extra = kids.length ? `\n\nIts add-ons go off with it: ${kids.join(", ")}.` : "";
      if (!window.confirm(`Switch off ${item.name} for your whole company? Nothing is deleted; people stop seeing it until it is switched back on.${extra}`)) return;
    }
    send(item, "state", { state: next, reason: `${next === "enabled" ? "Switched on" : "Switched off"} from the admin panel` });
  };
  // Entry 20 review: fetched with the company header, so the file is the company on this screen
  // (the one in a support session), not the operator's own; a plain link could not send it.
  const download = async (item) => {
    setError("");
    try { await apiDownload(`/EnterpriseCore/plugins/${encodeURIComponent(item.code)}/export/`, `${item.code}-export.zip`); }
    catch (err) { setError(err?.payload?.message || err?.message || "The download did not start."); }
  };
  const uninstall = (item) => {
    if (!window.confirm(`Uninstall ${item.name}? Its data is kept and can be downloaded or switched back on later.`)) return;
    send(item, "state", { state: "uninstalled", reason: "Uninstalled from the admin panel" });
  };
  const purge = (item) => {
    const typed = window.prompt(`This deletes all of ${item.name}'s data for your company, for good. A copy is saved first. Type the product's name to confirm:\n\n${item.name}`);
    if (typed === null) return;
    send(item, "purge", { confirm: typed });
  };

  const status = (item, parent) => {
    if (item.kind === "platform") return <Badge variant="info">Always on</Badge>;
    if (item.availability === "coming_soon") return <Badge>Coming soon</Badge>;
    if (parent && !item.parentEnabled) return <Badge>Off with {parent.name}</Badge>;
    if (item.enabled) return <Badge variant="success" dot>On</Badge>;
    if (item.state === "uninstalled") return <Badge>Uninstalled, data kept</Badge>;
    if (!item.inPlan) return <Badge variant="warning">Not included, ask us</Badge>;
    return <Badge variant="danger">Off</Badge>;
  };

  const row = (item, parent = null) => {
    const manage = data?.canManage && item.kind !== "platform" && item.availability === "available";
    const blockedByParent = parent && !item.parentEnabled;
    const kids = item.kind === "product" ? addonsOf(item.code) : [];
    return (
      <div key={item.code} className="ds-toolbar" style={{ justifyContent: "space-between", padding: "10px 0", paddingLeft: parent ? 28 : 0, borderTop: "1px solid var(--c-100, #eee)", opacity: blockedByParent ? 0.6 : 1, flexWrap: "wrap", gap: 8 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: parent ? 500 : 600 }}>
            {kids.length > 0 && (
              <button type="button" onClick={() => setOpen((o) => ({ ...o, [item.code]: !o[item.code] }))}
                style={{ marginRight: 6, background: "none", border: 0, cursor: "pointer", padding: 0 }}
                aria-label={open[item.code] ? "Hide add-ons" : "Show add-ons"}>
                {open[item.code] ? "▾" : "▸"}
              </button>
            )}
            {parent ? "↳ " : ""}{item.name}
            {kids.length > 0 && <span className="ds-muted" style={{ fontWeight: 400, fontSize: 12 }}> · {kids.filter((k) => k.enabled).length} of {kids.length} add-ons on</span>}
          </div>
          <div className="ds-card__sub">{item.summary}</div>
        </div>
        <div className="ds-toolbar" style={{ gap: 8, flexShrink: 0, flexWrap: "wrap" }}>
          {status(item, parent)}
          {item.canToggle && !blockedByParent && (
            <Button size="sm" variant={item.enabled ? "ghost" : "primary"} disabled={busyCode === item.code} onClick={() => toggle(item)}>
              {busyCode === item.code ? "Saving…" : item.enabled ? "Switch off" : "Switch on"}
            </Button>
          )}
          {manage && <Button size="sm" variant="ghost" onClick={() => download(item)}>Download data</Button>}
          {manage && !item.enabled && item.state === "disabled" && (
            <Button size="sm" variant="ghost" disabled={busyCode === item.code} onClick={() => uninstall(item)}>Uninstall</Button>
          )}
          {data?.canManage && item.state === "uninstalled" && (
            <Button size="sm" variant="danger" disabled={busyCode === item.code} onClick={() => purge(item)}>Delete data for good</Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {error && <p className="ds-field__error">{error}</p>}
      <Card title="Modules and add-ons" subtitle="Switching a module off hides it, and its add-ons, for everyone in your company. Nothing is deleted.">
        <input className="ds-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search modules and add-ons" style={{ marginBottom: 8, maxWidth: 360 }} />
        {!data && !error && <p className="ds-muted">Loading…</p>}
        {products.map((p) => {
          const kids = addonsOf(p.code).filter(matches);
          if (query && !matches(p) && kids.length === 0) return null;
          const showKids = query ? kids.length > 0 : open[p.code];
          return (
            <React.Fragment key={p.code}>
              {row(p)}
              {showKids && (query ? kids : addonsOf(p.code)).map((a) => row(a, p))}
            </React.Fragment>
          );
        })}
        {query && data && products.every((p) => !matches(p) && addonsOf(p.code).every((a) => !matches(a))) && (
          <p className="ds-muted">No module matches.</p>
        )}
      </Card>
      <Card title="Always included" subtitle="Part of every company's setup.">
        {platform.filter(matches).map((p) => row(p))}
      </Card>
    </>
  );
}

// Company settings (S3b): branding, locale and the company-level workflow switches.
// Every value starts at what the company had before; changing one affects only this
// company. Security protections are platform-wide and are not listed here on purpose.
export function CompanySettingsCard({ onChanged }) {
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [savingKey, setSavingKey] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(() => {
    apiGet("/EnterpriseCore/company-settings/")
      .then((res) => { setData(res); setDrafts({}); })
      .catch((err) => setError(err?.payload?.message || err?.message || "Could not load settings."));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (item, value) => {
    setSavingKey(item.key);
    setError("");
    try {
      await apiPost("/EnterpriseCore/company-settings/", { key: item.key, value });
      load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err?.payload?.message || err?.message || "That setting was not saved.");
    } finally {
      setSavingKey("");
    }
  };

  if (!data) return error ? <Card title="Company settings"><p className="ds-field__error">{error}</p></Card> : null;
  const groups = [];
  for (const item of data.settings || []) {
    let g = groups.find((x) => x.name === item.group);
    if (!g) { g = { name: item.group, items: [] }; groups.push(g); }
    g.items.push(item);
  }
  const editable = !!data.canChange;
  // Entry 20 review: the server says per item who may change it. The audit switches are the
  // platform operator's, so a company admin sees them read-only rather than a button that fails.
  const canEdit = (item) => !!item.editable;

  const control = (item) => {
    if (item.kind === "bool") {
      return (
        <Button size="sm" variant={item.value ? "primary" : "ghost"} disabled={!canEdit(item) || savingKey === item.key}
          onClick={() => save(item, !item.value)}>
          {item.value ? "On" : "Off"}
        </Button>
      );
    }
    const draft = drafts[item.key] ?? (item.value ?? "");
    if (item.kind === "choice") {
      return (
        <select className="ds-input" value={draft} disabled={!canEdit(item) || savingKey === item.key}
          onChange={(e) => save(item, e.target.value)} style={{ minWidth: 120 }}>
          {item.choices.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      );
    }
    return (
      <div className="ds-toolbar" style={{ gap: 8 }}>
        <input className="ds-input" value={draft} disabled={!canEdit(item)}
          onChange={(e) => setDrafts((d) => ({ ...d, [item.key]: e.target.value }))} style={{ minWidth: 220 }} />
        {canEdit(item) && String(draft) !== String(item.value ?? "") && (
          <Button size="sm" variant="primary" disabled={savingKey === item.key || (item.kind === "int" && wholeNumberDraft(draft) === null)}
            onClick={() => save(item, item.kind === "int" ? wholeNumberDraft(draft) : draft)}>
            {savingKey === item.key ? "Saving…" : "Save"}
          </Button>
        )}
      </div>
    );
  };

  return (
    <Card title="Company settings" subtitle={editable ? "Changes apply to your company only." : "Ask your company's admin to change these."}>
      {error && <p className="ds-field__error">{error}</p>}
      {groups.map((g) => (
        <div key={g.name} style={{ marginTop: 12 }}>
          <div className="ds-muted" style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>{g.name}</div>
          {g.items.map((item) => (
            <div key={item.key} className="ds-toolbar" style={{ justifyContent: "space-between", padding: "8px 0", borderTop: "1px solid var(--c-100, #eee)" }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 500 }}>{item.label}</div>
                {item.help && <div className="ds-card__sub">{item.help}</div>}
                {editable && item.operatorOnly && !canEdit(item) && <div className="ds-card__sub">Set by the platform operator.</div>}
              </div>
              {control(item)}
            </div>
          ))}
        </div>
      ))}
    </Card>
  );
}

